import { supabase, SUPABASE_URL } from './supabase';

export interface MealPlanWindow {
  id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  cycle_length: number | null;
  status: string | null;
  is_blank: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface MealInDay {
  id: string;
  recipeId: string;
  addedAt: string;
  sortOrder: number;
}

export interface MealPlanDay {
  id: string;
  window_id: string;
  plan_date: string;
  meals: MealInDay[] | null;
  planned_calories: number | null;
  is_custom: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface DayPlan {
  id: string;
  date: string;
  meals: { id: string; recipe: { id: string; title: string; image: string; calories: number; prepTime: number } | null }[];
  plannedCalories: number;
}

export async function fetchActiveWindow(userId: string): Promise<MealPlanWindow | null> {
  const today = new Date().toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('meal_plan_windows')
    .select('*')
    .eq('user_id', userId)
    .gte('end_date', today)
    .order('start_date', { ascending: true })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching meal plan window:', error);
    return null;
  }

  return data;
}

export async function fetchMealPlanDays(windowId: string): Promise<MealPlanDay[]> {
  const { data, error } = await supabase
    .from('meal_plan_days')
    .select('*')
    .eq('window_id', windowId)
    .order('plan_date', { ascending: true });

  if (error) {
    console.error('Error fetching meal plan days:', error);
    return [];
  }

  return data || [];
}

function buildImageUrl(recipeId: string): string {
  if (!recipeId || !SUPABASE_URL) return '';
  const imagePath = `recipes:${recipeId}:main.png`;
  return `${SUPABASE_URL}/storage/v1/object/public/recipe-images/${imagePath}?quality=50`;
}

export interface RecipeForMeal {
  id: string;
  title: string;
  image: string;
  calories: number;
  prepTime: number;
}

export async function fetchRecipesForMeals(recipeIds: string[]): Promise<Map<string, RecipeForMeal>> {
  if (recipeIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('recipes')
    .select('id, title, calories, prep_time')
    .in('id', recipeIds);

  if (error) {
    console.error('Error fetching recipes:', error);
    return new Map();
  }

  return new Map((data || []).map(r => [r.id, {
    id: r.id,
    title: r.title,
    image: buildImageUrl(r.id),
    calories: r.calories || 0,
    prepTime: r.prep_time || 0,
  }]));
}

export function organizeDaysWithRecipes(
  days: MealPlanDay[],
  recipeMap: Map<string, RecipeForMeal>
): DayPlan[] {
  return days.map((day) => {
    const dayMeals = (day.meals || []).sort((a, b) => a.sortOrder - b.sortOrder);
    
    const meals = dayMeals.map((meal) => {
      const recipe = recipeMap.get(meal.recipeId);
      return {
        id: meal.id,
        recipe: recipe ? {
          id: recipe.id,
          title: recipe.title,
          image: recipe.image,
          calories: recipe.calories,
          prepTime: recipe.prepTime,
        } : null,
      };
    });

    return {
      id: day.id,
      date: day.plan_date,
      meals,
      plannedCalories: day.planned_calories || meals.reduce((sum, m) => sum + (m.recipe?.calories || 0), 0),
    };
  });
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function isToday(dateString: string): boolean {
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

export function formatDateShort(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
